import React, {ClipboardEvent, useEffect, useState} from 'react';
import {
    ContactFormDataSend,
    ContactFormDataValues,
    PopupSetupType,
    SalesForceContactFormType,
    SFContactWrapper,
} from "../interfaces";
import {sendForm} from "../DAL/data-service";
import {getIsChina} from "../../../../domain/hidden-data-provider";
import {
    createControl,
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl,
    validateForm
} from "../../../../domain/validation/validation";
import {getSimpleCaptchaValidationError} from "../../../../domain/captcha/simple-captcha";
import {IFormControl} from "../../../../ui/forms/interfaces";
import {
    getRecaptcha2Error,
    getRecaptchaToken,
    renderRecaptcha2,
    resetRecaptcha2
} from "../../../../domain/captcha/recaptcha-provider";
import ChinaCaptchaField from "../../../../ui/forms/fields/captcha-china-field";
import Recaptcha3Text from "../../../../domain/captcha/recaptcha3-terms";
import FormTextField from "../../../../ui/forms/fields/text-field";
import {sendGAEvent} from "../../../../domain/marketing/gtm-provider";
import {ProcessResult, ProcessResultType} from "../../../../domain/process-result/interfaces";
import FormTextAreaField from "../../../../ui/forms/fields/textarea-field";
import SelectReactField from "../../../../ui/forms/fields/select-react-field";
import SalesForceContactPopUp from "./SalesForceContactPopUp";
import {useAppDispatch, useAppSelector} from "../DAL/store";
import {rootActions} from "../../../account/my-profile/DAL/store";
import {cloneDeep} from "lodash";
import {getReturnURL} from "../../../../domain/url-provider";

interface IEventsContactProps {
    /*sfFormData: ContactFormDataSend,
    sfFormDataEX: ContactFormDataEx,
    phoneCountries: OptionLabelValue[],
    setupList: OptionLabelValue[]*/
}

/**
 * the entry point
 */
const SalesForceContactApp= (props: IEventsContactProps) => {

    const isChina = getIsChina();
    //---------------------------------------------------
    const sfFormData_Store = useAppSelector((store) => store.sfFormData);
    const sfFormDataEX_Store = useAppSelector((store) => store.sfFormDataEX);
    //console.log('ContactApp sfFormData_Store: ',sfFormData_Store);
    const phoneCountries_Store = useAppSelector((store) => store.phoneCountries);
    const setupList_Store = useAppSelector((store) => store.setupList);
    //---------------------------------------------------
    const popupSetup = useAppSelector((store) => store.popupSetup);
    const dispatch = useAppDispatch();

    //console.log('sfFormData: ',sfFormData_Store);
    // validation ---------------------------------------
    const emailControl = createControl('email', sfFormData_Store.email, {
        required: true,
        email: true
    });
    const fullNameControl = createControl('fullName', sfFormData_Store.fullName, {
        required: true,
        maxlength: 80,
        name: true
    });
    const subjectControl = createControl('subject', sfFormData_Store.subject, {
        required: true,
        //regex: '^[\x00-\x7F\\ã’´]*$',
        //regexMessage:'Only Latin characters are supported.',
        minlength: 5,
        maxlength: 255,
        minlengthMessage: 'Please enter at least 5 characters.'
    });
    const serialNumberControl = createControl('serial-number', '', {
            required: false,
            regex: '^([a-zA-Z0-9-]){14,40}$',
            regexMessage:'Invalid serial number.',
            minlength: 14,
            minlengthMessage: 'Please enter at least 14 characters.',
            maxlength: 40,
        });

    const messageControl = createControl('message', sfFormData_Store.message, {
        required: true,
        //regex: '^[\x00-\x7F\\ã’´]*$',
        //regexMessage:'Only Latin characters are supported.',
        minlength: 20,
        maxlength: 32000,
        minlengthMessage: 'Please enter at least 20 characters.'
    });

    //console.log('phoneCountries_Store: ',phoneCountries_Store);
    var defVal = phoneCountries_Store.find(f=>f.value === sfFormData_Store.phone1Country2Letter);
    const phoneCountriesControl = createControl('ddlPhoneCountries', defVal ?? [], {
        required: (currentControl) => {
            if(!currentControl) return false;

            const current = currentControl.value;
            if(!current) return false;

            return current.value !== undefined;
        }
    });

    const setupListControl = createControl('ddlSetupList', setupList_Store.length === 1 ? setupList_Store[0] : [], {
        required: (currentControl) => {
            if(!currentControl) return false;

            const current = currentControl.value;
            if(!current) return false;

            return current.value !== undefined;
        }
    });

    const phoneNumber1Control = createControl('phoneNumber1', sfFormData_Store?.phoneNumber1 === 0 ? "" : sfFormData_Store?.phoneNumber1?.toString(), {
        required: true,
        phoneMessage: 'Please enter a valid number, use digits only.',
        int:true,
        intMessage: 'Please enter a valid number, use digits only.',
        minlength: 5,
        minlengthMessage: 'Please enter at least 5 digits.',

        maxlength: 12,
    });

    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });
    const [isValid,setIsValid] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    //const [response, setResponse] = useState<IServerValidation|null>();
    const [ResultData, setResultData] = useState<ProcessResult | null>();

    const [email, setEmail] = useState<IFormControl>(emailControl);
    const [fullName, setFullName] = useState<IFormControl>(fullNameControl);
    const [subject, setSubject] = useState<IFormControl>(subjectControl);
    const [originRadio, setOriginRadio] = useState(sfFormData_Store?.contactFormDataValues?.at(0));
    const [message, setMessage] = useState<IFormControl>(messageControl);
    const [serialNumber, setSerialNumber] = useState<IFormControl>(serialNumberControl);
    const [phoneCountries, setPhoneCountries] = useState<IFormControl>(phoneCountriesControl);
    const [setupList, setSetupList] = useState<IFormControl>(setupListControl);
    const [phoneNumber1, setPhoneNumber1] = useState<IFormControl>(phoneNumber1Control);

    // simple captcha - used for china
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /**
     * load initial data (drop-downs content) on page load once
     */
    useEffect(() => {
        (async () => {
            setIsLoading(false);

            //check if SetupList Updated
            const newSetup = setupList_Store.find(item => (item.id === 'new' || item.id === 'updated'));
            if(newSetup !== undefined) {
                //console.log('setupList: ',setupList);

                const updatedControl = updateControl(setupList, newSetup, true);
                setSetupList(updatedControl);
                //setupList.value = [newSetup];

                var _setupList_Store = cloneDeep(setupList_Store);
                _setupList_Store.forEach(item => {
                    item.id = item.value;
                });
                dispatch(
                    rootActions.main({
                        setupList:  _setupList_Store
                    })
                );
            }
        })();
    }, [setupList_Store]);
    useEffect(() => {
        (async () => {
            if(isChina && popupSetup === PopupSetupType.Hidden)
                setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        })();
    }, [popupSetup]);

    /**
     * handle save changes
     */
    const regUser = async () => {

        const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

        if(!captchaToken){
            setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
            setIsLoading(false);
            return;
        }

        var validateArray:any = [[fullName, setFullName],[subject, setSubject],[email, setEmail],[message, setMessage]];
        if(originRadio?.inquiryType === 'lv1-classic'){
            validateArray.push([serialNumber, setSerialNumber]);
        }
        if(sfFormData_Store.pageType === SalesForceContactFormType.Support){
            validateArray.push([phoneCountries, setPhoneCountries]);
            validateArray.push([phoneNumber1, setPhoneNumber1]);

            if(!sfFormData_Store.isGenericAccountID && originRadio?.setupsType !== undefined && originRadio?.setupsType !== '')
                validateArray.push([setupList, setSetupList]);
            else{//remove from validate if setups is hidden
                // @ts-ignore
                const index = validateArray.findIndex(([field]) => field.id === "ddlSetupList");
                if (index !== -1) {
                    validateArray.splice(index, 1);
                }
            }
            console.log('validateArray: ',validateArray);
        }
        if(isChina){
            validateArray.push([simpleCaptchaCode, setSimpleCaptchaCode]);
        }


        const isValidate =validateForm(validateArray);
        setIsValid(isValidate);
        if(!isValidate) return;

        const formDataSend: ContactFormDataSend = {
            fullName : fullName.value,
            subject : subject.value,
            email : email.value,
            originRadio: originRadio?.codeName ?? "",
            message: message.value,
            lV1_Classic_Serial_Number__c: originRadio?.inquiryType === 'lv1-classic' ? serialNumber.value : "",
            phone1Country2Letter:phoneCountries?.value?.value,
            setupID:setupList?.value?.value,
            phoneNumber1:phoneNumber1?.value === '' ? 0 : phoneNumber1?.value,
            pageType: sfFormData_Store.pageType,

        };
        const sfWrap:SFContactWrapper = {
            action: '',
            /*subEventID: subEvent?.value?.value,*/
            captchaCode: captchaToken,
            recaptchaVersion: recaptchaVersion,
            pageType: sfFormData_Store.pageType,
            contactFormDataSend: formDataSend,
        }

        setIsLoading(true);
        const response = await sendForm(sfWrap);

        sendGAEvent('Send Form', 'SendForm');

        if(!response || response.statusCode !== 200) {
            //setError(response.data as unknown as ModelServerErrors);
            return;
        }
        setIsLoading(false);

        //------------------------------------------------------------------------REDIRECT if not loggined
        if(response.data.resultEXType === "REDIRECT-LOGIN") {
            window.location.href = getReturnURL();
            return;
        }


        //------------------------------------------------------------------------recaptcha-error
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.resultEXType === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }
        //------------------------------------------------------------------------REDIRECT if success
        if(response.data.resultEXType === "REDIRECT")
            window.location.href = response.data.resultEX.toString();


       /* if(response.data.resultObjectType === "REFRESH") {
            window.location.reload();
            return;
        }*/

        //------------------------------------------------------------------------Error message
        if(response.data.message !== '')
            setResultData(response.data);
        //console.log('response.data: ',response.data);
    };
    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        //setResponse(null);
        setCaptchaError('');
    };
    const SetOriginRadio = (val:ContactFormDataValues) =>{
        //console.log('SetOriginRadio: ',val);
        setOriginRadio(val);
    };

    const ShowPopUp = (type:PopupSetupType, setupId:string) => {
        dispatch(
            rootActions.main({
                popupSetup:  type,
                popupSetupId:setupId
            })
        );
    }
    return (
        <div className="my-4">

            {
                <>

                    {/*<div className="mb-4">: isValid:{isValid?.toString()}</div>*/}
                    <FormTextField
                        id={'email-tb'}
                        label={'Email'}
                        control={email}
                        maxLength={80}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setEmail(updateControl(email, $target.value, !email.isValid));
                            //setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setEmail(validateControl(email));
                        }}
                        onClick={handleFieldClick}
                        /*onKeyUp={ handleEnter }*/
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />
                    <FormTextField
                        id={'first-name-tb'}
                        label={'Full Name'}
                        control={fullName}
                        maxLength={80}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setFullName(updateControl(fullName, $target.value, !fullName.isValid));
                            //setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setFullName(validateControl(fullName));
                        }}
                        onClick={handleFieldClick}
                        /*onKeyUp={ handleEnter }*/
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setFullName(updateControl(fullName, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />


                    {sfFormData_Store.pageType === SalesForceContactFormType.Support &&
                        <div className="flex">
                            <div className="w-full">
                                <SelectReactField
                                    onChange={
                                        (option) => {
                                            //console.log(option);
                                            const updatedControl = updateControl(phoneCountries, option, true);
                                            //('updatedControl', updatedControl)
                                            setPhoneCountries(updatedControl);
                                            //setResponse(null); // empty server error
                                            setCaptchaError('');
                                        }
                                    }
                                    onBlur={(option) => {
                                        //setSububEvent(updateControl(phoneCountries, option, !phoneCountries.isValid));
                                    }}
                                    label={"Country"}
                                    options={phoneCountries_Store}
                                    selectIdControl={phoneCountries}
                                    idPrefix={"ddlphoneCountries"}
                                />
                            </div>
                            <div className="w-full pl-2">
                                <FormTextField
                                    id={'phone-tb'}
                                    label={'Phone'}
                                    control={phoneNumber1}
                                    maxLength={100}
                                    onChange={evt => {
                                        const $target = evt.target as HTMLInputElement;
                                        setPhoneNumber1(updateControl(phoneNumber1, $target.value, !phoneNumber1.isValid));
                                        //setResponse(null); // empty server error
                                        setCaptchaError('');
                                    }}
                                    onBlur={evt => {
                                        setPhoneNumber1(validateControl(phoneNumber1));
                                    }}
                                    onClick={handleFieldClick}
                                    /*onKeyUp={ handleEnter }*/
                                    onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                                        setPhoneNumber1(updateControl(phoneNumber1, removeInvisibleCharsOnPaste(evt)));
                                    }}
                                />
                            </div>
                        </div>
                    }

                    {sfFormData_Store.pageType !== SalesForceContactFormType.Nx &&
                        <div className="mb-4">
                            <div id="inquiry-label" className="text-base mb-3" >My inquiry is regarding</div>
                            <ul className={`text-base`} aria-labelledby="inquiry-label">
                                {
                                    sfFormData_Store?.contactFormDataValues !== undefined && sfFormData_Store?.contactFormDataValues?.map((rItem, index) => {
                                        return <li className="" key={index}>
                                            <label className="cursor-pointer flex inline-block pb-1 w-fit"
                                            >
                                                <input
                                                    type="radio"
                                                    name="OriginRadio"
                                                    className={`mr-2 cursor-pointer h-1`}
                                                    value={rItem.codeName}
                                                    checked={originRadio?.codeName === rItem.codeName}
                                                    onClick={() => {
                                                        SetOriginRadio(rItem);
                                                    }}
                                                    onChange={() => {
                                                    }}
                                                />
                                                {rItem.displayName}
                                            </label>
                                        </li>
                                    })}
                            </ul>
                            {originRadio?.inquiryType === 'lv1-classic' &&
                                <div className="w-full pt-4">
                                    <FormTextField
                                        id={'lv1-classic-tb'}
                                        label={'LV1 Classic Serial Number'}
                                        control={serialNumber}
                                        maxLength={100}
                                        onChange={evt => {
                                            const $target = evt.target as HTMLInputElement;
                                            setSerialNumber(updateControl(serialNumber, $target.value, !serialNumber.isValid));
                                            //setResponse(null); // empty server error
                                            setCaptchaError('');
                                        }}
                                        onBlur={evt => {
                                            setSerialNumber(validateControl(serialNumber));
                                        }}
                                        onClick={handleFieldClick}
                                        /*onKeyUp={ handleEnter }*/
                                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                                            setSerialNumber(updateControl(serialNumber, removeInvisibleCharsOnPaste(evt)));
                                        }}
                                    />
                                </div>
                            }
                        </div>

                    }
                    {/*-----------------------------------------------------------------------------------Support Setup START*/}
                    {sfFormData_Store.pageType === SalesForceContactFormType.Support
                        && !sfFormData_Store.isGenericAccountID
                        && originRadio?.setupsType !== undefined
                        && originRadio?.setupsType !== '' &&
                        <div className="flex flex-col md:flex-row items-start">
                            <div className="w-full md:w-1/2 flex items-center">
                                <div className="w-full">
                                    <SelectReactField
                                        onChange={
                                            (option) => {
                                                //console.log(option);
                                                const updatedControl = updateControl(setupList, option, true);
                                                //console.log('updatedControl', updatedControl)
                                                setSetupList(updatedControl);
                                                //setResponse(null); // empty server error
                                                setCaptchaError('');
                                            }
                                        }
                                        onBlur={(option) => {
                                            //setSububEvent(updateControl(setupList, option, !setupList.isValid));
                                        }}
                                        label={"Existing Setups"}
                                        options={setupList_Store}
                                        selectIdControl={setupList}
                                        idPrefix={"ddlSetupList"}
                                    />
                                </div>
                            </div>
                            <div className="w-full md:w-1/2 flex flex-col md:flex-row items-start md:items-center mb-6">
                                <button type="button" aria-haspopup="dialog" aria-controls="add-edit-dialod" className="md:mt-0 md:ml-4 w-btn w-btn-outline-black w-40"
                                        onClick={evt => {
                                            setResultData(null);
                                            ShowPopUp(PopupSetupType.ShowAdd, "");
                                            //console.log('Add New Setup');
                                        }}>Add New Setup
                                </button>

                                {/*-----------------------------------------------EDIT BUTTON*/}
                                {!(Array.isArray(setupList.value) && setupList.value.length === 0) &&
                                    <button type="button"  aria-haspopup="dialog" aria-controls="add-edit-dialod"
                                            className="mt-4 md:mt-0 md:ml-4 w-btn w-btn-outline-black w-40"
                                            onClick={evt => {
                                                setResultData(null);
                                                ShowPopUp(PopupSetupType.ShowEdit, setupList.value.value);
                                                //console.log('Edit Setup');
                                            }}>Edit Setup
                                    </button>
                                }
                                {(Array.isArray(setupList.value) && setupList.value.length === 0) &&
                                    <button type="button" aria-haspopup="dialog" aria-controls="add-edit-dialod"
                                            className="mt-4 md:mt-0 md:ml-4 w-btn w-40 border border-grey-400 text-gray-400 bg-white disabled cursor-not-allowed"
                                            onClick={evt => {
                                                return;
                                            }}>Edit Setup
                                    </button>
                                }

                                {/*-----------------------------------------------CLONE BUTTON*/}
                                {!(Array.isArray(setupList.value) && setupList.value.length === 0) &&
                                    <button type="button"
                                            className="mt-4 md:mt-0 md:ml-4 w-btn w-btn-outline-black w-40"
                                            onClick={evt => {
                                                setResultData(null);
                                                ShowPopUp(PopupSetupType.ShowClone, setupList.value.value);
                                                console.log('Clone Setup');
                                            }}>Clone Setup
                                    </button>
                                }
                                {(Array.isArray(setupList.value) && setupList.value.length === 0) &&
                                    <button type="button"
                                            className="mt-4 md:mt-0 md:ml-4 w-btn w-40 border border-grey-400 text-gray-400 bg-white disabled cursor-not-allowed">
                                        Clone Setup
                                    </button>
                                }
                            </div>
                        </div>
                    }
                    {/*-----------------------------------------------------------------------------------Support Setup END*/}
                    <FormTextField
                        id={'subject-tb'}
                        label={'Subject'}
                        control={subject}
                        maxLength={100}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setSubject(updateControl(subject, $target.value, !subject.isValid));
                            //setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setSubject(validateControl(subject));
                        }}
                        onClick={handleFieldClick}
                        /*onKeyUp={ handleEnter }*/
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setSubject(updateControl(subject, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />
                    <FormTextAreaField
                        id={'message-tb'}
                        label={'Message'}
                        control={message}
                        maxLength={524288}
                        cols={4}
                        rows={7}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setMessage(updateControl(message, $target.value, !message.isValid));
                            //setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setMessage(validateControl(message));
                        }}
                        onClick={handleFieldClick}
                        /*onKeyUp={ handleEnter }*/
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setMessage(updateControl(message, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />
                    <div>
                        {!isLoading && <button type="button" className="w-btn w-40"
                                               onClick={evt => {
                                                   setResultData(null);
                                                   regUser();
                                               }}>Send
                        </button>
                        }
                        {isLoading && <button type="button" className="w-btn w-40 bg-gray-200"
                                              onClick={evt => {
                                              }}>Send
                        </button>}
                    </div>
                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha"
                             className={`my-4 flex justify-center md:justify-start recaptcha recaptcha-version-${recaptchaVersion}`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={simpleCaptchaCode}
                            setSimpleCaptchaCode={setSimpleCaptchaCode}
                            simpleCaptchaAttempts={simpleCaptchaAttempts}
                        />
                    }
                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center"
                             data-id={recaptcha2WidgetID}>{captchaError}</div>
                    }

                    {
                        isValid !== null && !isValid &&
                        <span></span>
                    }
                    {/*
                        isValid !== null && !isValid &&
                        <div className ="mt-4 error-box">
                            Data empty or wrong
                        </div>
                    */}
                    {ResultData?.result === ProcessResultType.Fail && ResultData?.message != null && ResultData?.message.toString() !== "" &&

                        <div className="my-4 error-box" role="alert"
                             dangerouslySetInnerHTML={{__html: ResultData?.message?.toString()}}/>

                    }
                    {ResultData?.result === ProcessResultType.Success && ResultData?.message != null && ResultData?.message.toString() !== "" &&

                        <div className="mt-4 ok-box" role="alert"
                             dangerouslySetInnerHTML={{__html: ResultData?.message?.toString()}}/>

                    }
                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-4 text-center">
                            <Recaptcha3Text/>
                        </div>
                    }
                </>
            }
            {/*----------------------------------------------------------------------setupsPopup*/}
            {popupSetup !== PopupSetupType.Hidden &&
                <SalesForceContactPopUp setupsDataWrap={sfFormDataEX_Store.setupsDataWrap}
                                        setupsType={originRadio?.setupsType ?? ""}/>
            }
        </div>
    )
};

export default SalesForceContactApp;